import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Alert,
  Chip,
  CircularProgress,
  Avatar,
  Divider,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Group as GroupIcon,
  Settings as SettingsIcon,
  People as PeopleIcon,
  PersonRemove as PersonRemoveIcon,
  Block as BlockIcon,
  Close as CloseIcon,
  AdminPanelSettings as AdminIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import {
  collection,
  addDoc,
  deleteDoc,
  doc,
  getDocs,
  updateDoc,
  serverTimestamp,
  getDoc,
  setDoc
} from 'firebase/firestore';
import { db } from '../config/firebase';

interface Group {
  id: string;
  name: string;
  description: string;
  memberCount: number;
  createdAt: any;
  adminOnlyPosting?: boolean;
}

interface GroupMember {
  userId: string;
  userDisplayName: string;
  joinedAt: any;
  isBanned?: boolean;
  isAdmin?: boolean;
}

interface AdminPanelProps {
  open: boolean;
  onClose: () => void;
}

export const AdminPanel: React.FC<AdminPanelProps> = ({ open, onClose }) => {
  const { currentUser } = useAuth();
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [membersDialogOpen, setMembersDialogOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
  const [groupMembers, setGroupMembers] = useState<GroupMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    adminOnlyPosting: false
  });

  // Check if current user is admin
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!currentUser) {
        console.log('AdminPanel: No current user');
        return;
      }
      
      console.log('AdminPanel: Checking admin status for user:', currentUser.uid);
      
      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const isUserAdmin = userData.isAdmin === true;
          console.log('AdminPanel: User data:', userData);
          console.log('AdminPanel: Is admin:', isUserAdmin);
          setIsAdmin(isUserAdmin);
        } else {
          console.log('AdminPanel: User document does not exist');
          setIsAdmin(false);
        }
      } catch (error) {
        console.error('AdminPanel: Error checking admin status:', error);
        setError('Failed to check admin status: ' + (error as Error).message);
      }
    };

    checkAdminStatus();
  }, [currentUser]);

  // Load groups
  useEffect(() => {
    if (!open || !isAdmin) {
      console.log('AdminPanel: Not loading groups - open:', open, 'isAdmin:', isAdmin);
      return;
    }

    const loadGroups = async () => {
      console.log('AdminPanel: Starting to load groups...');
      setLoading(true);
      setError(null);
      try {
        const groupsQuery = collection(db, 'groups');
        console.log('AdminPanel: Fetching groups from Firestore...');
        const groupsSnapshot = await getDocs(groupsQuery);
        console.log('AdminPanel: Found', groupsSnapshot.size, 'groups');
        
        const groupsData = await Promise.all(
          groupsSnapshot.docs.map(async (groupDoc) => {
            try {
              // Count members
              const membersQuery = collection(db, 'groups', groupDoc.id, 'members');
              const membersSnapshot = await getDocs(membersQuery);
              
              const groupData = {
                id: groupDoc.id,
                ...groupDoc.data(),
                memberCount: membersSnapshot.size
              };
              console.log('AdminPanel: Loaded group:', (groupData as any).name || 'Unknown', 'with', groupData.memberCount, 'members');
              return groupData;
            } catch (memberError) {
              console.error('AdminPanel: Error loading members for group', groupDoc.id, ':', memberError);
              return {
                id: groupDoc.id,
                ...groupDoc.data(),
                memberCount: 0
              };
            }
          })
        );
        
        console.log('AdminPanel: Setting groups data:', groupsData);
        setGroups(groupsData as Group[]);
      } catch (error) {
        console.error('AdminPanel: Error loading groups:', error);
        setError('Failed to load groups: ' + (error as Error).message);
      } finally {
        setLoading(false);
      }
    };

    loadGroups();
  }, [open, isAdmin]);

  const handleCreateGroup = async () => {
    if (!currentUser || !isAdmin) return;

    if (!formData.name.trim() || !formData.description.trim()) {
      setError('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      await addDoc(collection(db, 'groups'), {
        name: formData.name.trim(),
        description: formData.description.trim(),
        createdAt: serverTimestamp(),
        createdBy: currentUser.uid,
        adminOnlyPosting: formData.adminOnlyPosting
      });

      setFormData({ name: '', description: '', adminOnlyPosting: false });
      setCreateDialogOpen(false);
      setSuccess('Group created successfully');
      
      // Reload groups
      const groupsQuery = collection(db, 'groups');
      const groupsSnapshot = await getDocs(groupsQuery);
      const groupsData = await Promise.all(
        groupsSnapshot.docs.map(async (groupDoc) => {
          const membersQuery = collection(db, 'groups', groupDoc.id, 'members');
          const membersSnapshot = await getDocs(membersQuery);
          
          return {
            id: groupDoc.id,
            ...groupDoc.data(),
            memberCount: membersSnapshot.size
          };
        })
      );
      setGroups(groupsData as Group[]);
    } catch (error) {
      console.error('Error creating group:', error);
      setError('Failed to create group');
    } finally {
      setLoading(false);
    }
  };

  const handleEditGroup = async () => {
    if (!selectedGroup || !currentUser || !isAdmin) return;

    if (!formData.name.trim() || !formData.description.trim()) {
      setError('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      await updateDoc(doc(db, 'groups', selectedGroup.id), {
        name: formData.name.trim(),
        description: formData.description.trim(),
        adminOnlyPosting: formData.adminOnlyPosting
      });

      setFormData({ name: '', description: '', adminOnlyPosting: selectedGroup.adminOnlyPosting || false });
      setEditDialogOpen(false);
      setSelectedGroup(null);
      setSuccess('Group updated successfully');
      
      // Update local state
      setGroups(prev => prev.map(group => 
        group.id === selectedGroup.id 
          ? { ...group, name: formData.name.trim(), description: formData.description.trim(), adminOnlyPosting: formData.adminOnlyPosting }
          : group
      ));
    } catch (error) {
      console.error('Error updating group:', error);
      setError('Failed to update group');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteGroup = async (groupId: string) => {
    if (!currentUser || !isAdmin) return;

    const confirmDelete = window.confirm('Are you sure you want to delete this group? This action cannot be undone.');
    if (!confirmDelete) return;

    setLoading(true);
    try {
      await deleteDoc(doc(db, 'groups', groupId));
      setGroups(prev => prev.filter(group => group.id !== groupId));
      setSuccess('Group deleted successfully');
    } catch (error) {
      console.error('Error deleting group:', error);
      setError('Failed to delete group');
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (group: Group) => {
    setSelectedGroup(group);
    setFormData({
      name: group.name,
      description: group.description,
      adminOnlyPosting: group.adminOnlyPosting || false
    });
    setEditDialogOpen(true);
  };

  const openMembersDialog = async (group: Group) => {
    setSelectedGroup(group);
    setMembersDialogOpen(true);
    await loadGroupMembers(group.id);
  };

  const loadGroupMembers = async (groupId: string) => {
    console.log('AdminPanel: Starting to load members for group:', groupId);
    setMembersLoading(true);
    setError(null);
    
    try {
      console.log('AdminPanel: Fetching members collection...');
      const membersQuery = collection(db, 'groups', groupId, 'members');
      const membersSnapshot = await getDocs(membersQuery);
      console.log('AdminPanel: Found', membersSnapshot.size, 'members');
      
      console.log('AdminPanel: Fetching banned collection...');
      const bannedQuery = collection(db, 'groups', groupId, 'banned');
      const bannedSnapshot = await getDocs(bannedQuery);
      console.log('AdminPanel: Found', bannedSnapshot.size, 'banned users');
      
      const bannedUserIds = new Set(bannedSnapshot.docs.map(doc => doc.id));
      console.log('AdminPanel: Banned user IDs:', Array.from(bannedUserIds));
      
      // Fetch admin status for each member
      const membersData = await Promise.all(
        membersSnapshot.docs.map(async (memberDoc) => {
          try {
            // Get user document to check admin status
            const userDocRef = doc(db, 'users', memberDoc.id);
            const userDoc = await getDoc(userDocRef);
            const userData = userDoc.exists() ? userDoc.data() : {};
            const isUserAdmin = userData.isAdmin === true;
            
            const memberData = {
              userId: memberDoc.id,
              ...memberDoc.data(),
              isBanned: bannedUserIds.has(memberDoc.id),
              isAdmin: isUserAdmin
            };
            console.log('AdminPanel: Processing member:', memberData);
            return memberData;
          } catch (error) {
            console.error('AdminPanel: Error loading admin status for user', memberDoc.id, ':', error);
            return {
              userId: memberDoc.id,
              ...memberDoc.data(),
              isBanned: bannedUserIds.has(memberDoc.id),
              isAdmin: false
            };
          }
        })
      );
      
      console.log('AdminPanel: Final members data:', membersData);
      setGroupMembers(membersData as GroupMember[]);
    } catch (error) {
      console.error('AdminPanel: Error loading group members:', error);
      setError('Failed to load group members: ' + (error as Error).message);
    } finally {
      setMembersLoading(false);
    }
  };

  const handleRemoveUserFromGroup = async (groupId: string, userId: string, userName: string) => {
    const confirmRemove = window.confirm(`Are you sure you want to remove ${userName} from this group?`);
    if (!confirmRemove) return;

    try {
      await deleteDoc(doc(db, 'groups', groupId, 'members', userId));
      
      // Update local state
      setGroupMembers(prev => prev.filter(member => member.userId !== userId));
      
      // Update group member count in main list
      setGroups(prev => prev.map(group => 
        group.id === groupId 
          ? { ...group, memberCount: group.memberCount - 1 }
          : group
      ));
      
      setSuccess(`${userName} has been removed from the group`);
    } catch (error) {
      console.error('Error removing user from group:', error);
      setError('Failed to remove user from group');
    }
  };

  const handleToggleUserAdmin = async (userId: string, userName: string, isCurrentlyAdmin: boolean) => {
    const action = isCurrentlyAdmin ? 'remove admin privileges from' : 'promote to admin';
    const confirmAction = window.confirm(`Are you sure you want to ${action} ${userName}?`);
    if (!confirmAction) return;

    try {
      const userDocRef = doc(db, 'users', userId);
      await updateDoc(userDocRef, {
        isAdmin: !isCurrentlyAdmin
      });
      
      // Reload members to reflect admin status change
      if (selectedGroup) {
        await loadGroupMembers(selectedGroup.id);
      }
      
      setSuccess(`${userName} has been ${isCurrentlyAdmin ? 'demoted from admin' : 'promoted to admin'}`);
    } catch (error) {
      console.error(`Error ${isCurrentlyAdmin ? 'demoting' : 'promoting'} user:`, error);
      setError(`Failed to ${isCurrentlyAdmin ? 'demote' : 'promote'} user`);
    }
  };

  const handleBanUserFromGroup = async (groupId: string, userId: string, userName: string, isBanned: boolean) => {
    const action = isBanned ? 'unban' : 'ban';
    const confirmAction = window.confirm(`Are you sure you want to ${action} ${userName} from this group?`);
    if (!confirmAction) return;

    try {
      if (isBanned) {
        // Unban user
        await deleteDoc(doc(db, 'groups', groupId, 'banned', userId));
      } else {
        // Ban user
        await setDoc(doc(db, 'groups', groupId, 'banned', userId), {
          userId: userId,
          userDisplayName: userName,
          bannedAt: serverTimestamp(),
          bannedBy: currentUser?.uid
        });
        
        // Also remove from group if they're currently a member
        try {
          await deleteDoc(doc(db, 'groups', groupId, 'members', userId));
          // Update group member count
          setGroups(prev => prev.map(group => 
            group.id === groupId 
              ? { ...group, memberCount: Math.max(0, group.memberCount - 1) }
              : group
          ));
        } catch (memberError) {
          // User might not be a member, that's okay
          console.log('User was not a member, only banned');
        }
      }
      
      // Reload members to reflect ban status
      await loadGroupMembers(groupId);
      
      setSuccess(`${userName} has been ${action}ned ${isBanned ? 'from' : 'from'} the group`);
    } catch (error) {
      console.error(`Error ${action}ning user:`, error);
      setError(`Failed to ${action} user`);
    }
  };

  if (!isAdmin) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SettingsIcon sx={{ mr: 1 }} />
            Access Denied
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="error">
            You don't have admin privileges to access this panel.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <SettingsIcon sx={{ mr: 1 }} />
              Admin Panel - Group Management
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
            >
              Create Group
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <List>
              {groups.map((group) => (
                <ListItem key={group.id} divider>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <GroupIcon />
                        <Typography variant="h6">{group.name}</Typography>
                        <Chip 
                          label={`${group.memberCount} members`} 
                          size="small" 
                          variant="outlined" 
                        />
                      </Box>
                    }
                    secondary={group.description}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      onClick={() => openMembersDialog(group)}
                      sx={{ mr: 1 }}
                      title="Manage Members"
                    >
                      <PeopleIcon />
                    </IconButton>
                    <IconButton
                      edge="end"
                      onClick={() => openEditDialog(group)}
                      sx={{ mr: 1 }}
                      title="Edit Group"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      edge="end"
                      onClick={() => handleDeleteGroup(group.id)}
                      color="error"
                      title="Delete Group"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
              {groups.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color="text.secondary">
                    No groups created yet. Create your first group to get started.
                  </Typography>
                </Box>
              )}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Create Group Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Group</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Group Name"
            fullWidth
            variant="outlined"
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.adminOnlyPosting}
                onChange={(e) => setFormData(prev => ({ ...prev, adminOnlyPosting: e.target.checked }))}
                color="warning"
              />
            }
            label="Admin Only Posting"
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            When enabled, only admins can post messages in this group
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateGroup} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Group Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Group</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Group Name"
            fullWidth
            variant="outlined"
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.adminOnlyPosting}
                onChange={(e) => setFormData(prev => ({ ...prev, adminOnlyPosting: e.target.checked }))}
                color="warning"
              />
            }
            label="Admin Only Posting"
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            When enabled, only admins can post messages in this group
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleEditGroup} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Members Management Dialog */}
      <Dialog 
        open={membersDialogOpen} 
        onClose={() => setMembersDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <PeopleIcon sx={{ mr: 1 }} />
              Manage Members - {selectedGroup?.name}
            </Box>
            <IconButton onClick={() => setMembersDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {membersLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Total Members: {groupMembers.length} | 
                Admins: {groupMembers.filter(m => m.isAdmin).length} | 
                Banned: {groupMembers.filter(m => m.isBanned).length}
              </Typography>
              <List>
                {groupMembers.map((member, index) => (
                  <React.Fragment key={member.userId}>
                    <ListItem>
                      <Avatar sx={{ mr: 2 }}>
                        {member.userDisplayName?.[0]?.toUpperCase() || 'U'}
                      </Avatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle1">
                              {member.userDisplayName || 'Anonymous'}
                            </Typography>
                            {member.isAdmin && (
                              <Chip 
                                label="Admin" 
                                color="primary" 
                                size="small" 
                                variant="outlined"
                                icon={<AdminIcon />}
                              />
                            )}
                            {member.isBanned && (
                              <Chip 
                                label="Banned" 
                                color="error" 
                                size="small" 
                                variant="outlined"
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          member.joinedAt 
                            ? `Joined: ${member.joinedAt.toDate?.()?.toLocaleDateString() || 'Unknown'}`
                            : 'Join date unknown'
                        }
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          onClick={() => handleToggleUserAdmin(
                            member.userId, 
                            member.userDisplayName,
                            member.isAdmin || false
                          )}
                          color={member.isAdmin ? "error" : "primary"}
                          sx={{ mr: 1 }}
                          title={member.isAdmin ? "Remove Admin" : "Promote to Admin"}
                          disabled={member.userId === currentUser?.uid && member.isAdmin}
                        >
                          <AdminIcon />
                        </IconButton>
                        <IconButton
                          edge="end"
                          onClick={() => handleBanUserFromGroup(
                            selectedGroup!.id, 
                            member.userId, 
                            member.userDisplayName,
                            member.isBanned || false
                          )}
                          color={member.isBanned ? "primary" : "warning"}
                          sx={{ mr: 1 }}
                          title={member.isBanned ? "Unban User" : "Ban User"}
                        >
                          <BlockIcon />
                        </IconButton>
                        <IconButton
                          edge="end"
                          onClick={() => handleRemoveUserFromGroup(
                            selectedGroup!.id, 
                            member.userId, 
                            member.userDisplayName
                          )}
                          color="error"
                          title="Remove from Group"
                        >
                          <PersonRemoveIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                    {index < groupMembers.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
                {groupMembers.length === 0 && (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="text.secondary">
                      No members in this group yet.
                    </Typography>
                  </Box>
                )}
              </List>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMembersDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}; 